import { findLinkOpportunities, generateAlternativeAnchors } from '@/utils/linkAnalyzer';

export interface LinkingSuggestion {
  keyword: string;
  targetUrl: string;
  suggestedAnchor: string;
  priority: 'high' | 'medium' | 'low';
  context?: string;
}

export interface InternalLink {
  keyword: string;
  url: string;
  anchor: string;
  weight: number;
}

export interface RelatedPage {
  title: string;
  url: string;
  relevance: number;
  description?: string;
}

/**
 * Règles de maillage interne pour optimiser le SEO
 */
class LinkingRulesEngine {
  // Mapping des pages et leurs mots-clés cibles
  private pageKeywords: Record<string, string[]> = {
    '/': ['location benne', 'démolition', 'débarras', 'évacuation déchets', 'Aveyron'],
    '/nos-services/location-bennes': ['location benne', 'contenants déchets', 'évacuation gravats', 'bennes chantier'],
    '/nos-services/demolition': ['démolition', 'destruction bâtiment', 'abattage', 'démolition Aveyron'],
    '/debarras': ['débarras', 'vidage maison', 'nettoyage', 'évacuation mobilier'],
    '/terrassement': ['terrassement', 'excavation', 'nivellement', 'préparation terrain'],
    '/zones/aveyron': ['Aveyron', 'Rodez', 'Millau', 'location benne Aveyron'],
    '/zones/haute-garonne': ['Toulouse', 'Haute-Garonne', 'location benne Toulouse'],
    '/zones/tarn': ['Tarn', 'Albi', 'Castres', 'location benne Tarn'],
    '/contact': ['devis', 'contact', 'demande', 'information'],
    // Nouvelles URLs des sites
    '/site/profit-recyclage/rodez': ['Profit Recyclage', 'Rodez', 'recyclage Aveyron', 'métaux Rodez'],
    '/site/belle-recyclage/millau': ['Belle Recyclage', 'Millau', 'recyclage Millau', 'métaux Millau'],
    '/site/lescout-recyclage/lescout': ['Lescout Recyclage', 'location bennes Lescout', 'recyclage métaux'],
    '/site/rm-recyclage/castres': ['RM Recyclage', 'Castres', 'recyclage Tarn', 'métaux Castres']
  };

  // Liens contextuels par page
  private contextualLinks: Record<string, Array<{ text: string; url: string; anchor: string }>> = {
    '/': [
      { text: 'Découvrir nos services', url: '/nos-services/location-bennes', anchor: 'location de bennes' },
      { text: 'Zones d\'intervention', url: '/zones/aveyron', anchor: 'nos zones d\'intervention' },
      { text: 'Demander un devis', url: '/contact', anchor: 'obtenir un devis gratuit' }
    ],
    '/nos-services/location-bennes': [
      { text: 'Services connexes', url: '/nos-services/demolition', anchor: 'démolition professionnelle' },
      { text: 'Types de déchets', url: '/debarras', anchor: 'débarras et évacuation' },
      { text: 'Zone Aveyron', url: '/zones/aveyron', anchor: 'intervention en Aveyron' }
    ],
    '/nos-services/demolition': [
      { text: 'Évacuation gravats', url: '/nos-services/location-bennes', anchor: 'location de bennes pour gravats' },
      { text: 'Débarras post-démolition', url: '/debarras', anchor: 'services de débarras' },
      { text: 'Préparation terrain', url: '/terrassement', anchor: 'terrassement et nivellement' }
    ],
    '/debarras': [
      { text: 'Évacuation déchets', url: '/nos-services/location-bennes', anchor: 'bennes pour débarras' },
      { text: 'Démolition partielle', url: '/nos-services/demolition', anchor: 'démolition sélective' },
      { text: 'Zones d\'intervention', url: '/zones/aveyron', anchor: 'débarras en Aveyron' }
    ]
  };

  // Pages connexes par thématique
  private relatedPages: Record<string, RelatedPage[]> = {
    '/': [
      { title: 'Location de bennes - Tous types', url: '/nos-services/location-bennes', relevance: 0.9, description: 'Service principal de location' },
      { title: 'Démolition professionnelle', url: '/nos-services/demolition', relevance: 0.8, description: 'Services de démolition' },
      { title: 'Débarras et évacuation', url: '/debarras', relevance: 0.8, description: 'Services de débarras' },
      { title: 'Zones d\'intervention Aveyron', url: '/zones/aveyron', relevance: 0.7, description: 'Couverture géographique' }
    ],
    '/nos-services/location-bennes': [
      { title: 'Démolition et évacuation', url: '/nos-services/demolition', relevance: 0.85, description: 'Service complémentaire' },
      { title: 'Débarras professionnel', url: '/debarras', relevance: 0.8, description: 'Évacuation mobilier' },
      { title: 'Intervention Aveyron', url: '/zones/aveyron', relevance: 0.75, description: 'Zone principale' },
      { title: 'Intervention Toulouse', url: '/zones/haute-garonne', relevance: 0.7, description: 'Zone étendue' }
    ],
    '/nos-services/demolition': [
      { title: 'Bennes pour gravats', url: '/nos-services/location-bennes', relevance: 0.9, description: 'Évacuation nécessaire' },
      { title: 'Débarras post-démolition', url: '/debarras', relevance: 0.8, description: 'Nettoyage complet' },
      { title: 'Terrassement après démolition', url: '/terrassement', relevance: 0.75, description: 'Préparation terrain' },
      { title: 'Démolition Aveyron', url: '/zones/aveyron', relevance: 0.7, description: 'Zone d\'intervention' }
    ]
  };

  // Mots-clés à éviter pour les liens automatiques
  private excludedKeywords = ['le', 'la', 'les', 'un', 'une', 'des', 'de', 'du', 'et', 'ou', 'mais', 'donc', 'car', 'ni', 'or'];

  /**
   * Obtient les suggestions de liens pour une page donnée
   */
  getSuggestionsForPage(pathname: string): LinkingSuggestion[] {
    const suggestions: LinkingSuggestion[] = [];
    const currentPageKeywords = this.pageKeywords[pathname] || [];

    // Générer des suggestions basées sur le contenu de la page
    Object.entries(this.pageKeywords).forEach(([url, keywords]) => {
      if (url === pathname) return; // Éviter les liens vers la même page

      keywords.forEach(keyword => {
        if (this.shouldSuggestLink(keyword, currentPageKeywords, pathname)) {
          suggestions.push({
            keyword,
            targetUrl: url,
            suggestedAnchor: this.generateOptimizedAnchor(keyword, url),
            priority: this.calculatePriority(keyword, url, pathname)
          });
        }
      });
    });

    return suggestions.sort((a, b) => this.sortByPriority(a.priority, b.priority));
  }

  /**
   * Obtient les pages connexes pour une page donnée
   */
  getRelatedPages(pathname: string): RelatedPage[] {
    return this.relatedPages[pathname] || [];
  }

  /**
   * Obtient les liens contextuels pour une page donnée
   */
  getContextualLinks(pathname: string): Array<{ text: string; url: string; anchor: string }> {
    return this.contextualLinks[pathname] || [];
  }

  /**
   * Obtient des liens pour un mot-clé spécifique
   */
  getLinksForKeyword(keyword: string): InternalLink[] {
    const links: InternalLink[] = [];
    const lowerKeyword = keyword.toLowerCase();

    Object.entries(this.pageKeywords).forEach(([url, keywords]) => {
      keywords.forEach(pageKeyword => {
        if (pageKeyword.toLowerCase().includes(lowerKeyword) || 
            lowerKeyword.includes(pageKeyword.toLowerCase())) {
          links.push({
            keyword: pageKeyword,
            url,
            anchor: this.generateOptimizedAnchor(pageKeyword, url),
            weight: this.calculateWeight(keyword, pageKeyword)
          });
        }
      });
    });

    return links.sort((a, b) => b.weight - a.weight);
  }

  /**
   * Génère des ancres optimisées pour une URL
   */
  getOptimizedAnchors(targetUrl: string): string[] {
    const keywords = this.pageKeywords[targetUrl] || [];
    const anchors: string[] = [];

    keywords.forEach(keyword => {
      anchors.push(keyword);
      const alternatives = generateAlternativeAnchors(keyword, this.getCategoryFromUrl(targetUrl));
      anchors.push(...alternatives);
    });

    return [...new Set(anchors)].slice(0, 5);
  }

  /**
   * Analyse un texte pour trouver des opportunités de liens
   */
  analyzeTextForLinkOpportunities(text: string): LinkingSuggestion[] {
    const suggestions: LinkingSuggestion[] = [];
    const words = text.toLowerCase().split(/\s+/);

    Object.entries(this.pageKeywords).forEach(([url, keywords]) => {
      keywords.forEach(keyword => {
        const keywordWords = keyword.toLowerCase().split(/\s+/);
        const isPresent = keywordWords.every(word => words.includes(word));

        if (isPresent && !this.excludedKeywords.includes(keyword.toLowerCase())) {
          suggestions.push({
            keyword,
            targetUrl: url,
            suggestedAnchor: keyword,
            priority: 'medium'
          });
        }
      });
    });

    return suggestions;
  }

  /**
   * Génère des liens automatiques pour un contenu donné
   */
  generateAutomaticLinks(content: string, currentPath: string): InternalLink[] {
    const opportunities = findLinkOpportunities(content, currentPath);
    
    return opportunities.map(opp => ({
      keyword: opp.keyword,
      url: opp.suggestedUrl,
      anchor: opp.anchor,
      weight: opp.confidence
    })).filter(link => link.weight > 0.6); // Seuil de confiance minimum
  }

  // Méthodes privées

  private shouldSuggestLink(keyword: string, currentPageKeywords: string[], pathname: string): boolean {
    // Éviter les suggestions redondantes
    if (currentPageKeywords.some(kw => kw.toLowerCase().includes(keyword.toLowerCase()))) {
      return false;
    }

    // Éviter les mots-clés exclus
    if (this.excludedKeywords.includes(keyword.toLowerCase())) {
      return false;
    }

    return true;
  }

  private generateOptimizedAnchor(keyword: string, targetUrl: string): string {
    const category = this.getCategoryFromUrl(targetUrl);
    
    switch (category) {
      case 'location':
        return `${keyword} - Devis gratuit`;
      case 'demolition':
        return `Services de ${keyword}`;
      case 'zones':
        return `Intervention ${keyword}`;
      default:
        return keyword;
    }
  }

  private calculatePriority(keyword: string, targetUrl: string, currentPath: string): 'high' | 'medium' | 'low' {
    // Haute priorité pour les services principaux
    if (targetUrl.includes('location-bennes') || targetUrl.includes('demolition')) {
      return 'high';
    }

    // Priorité moyenne pour les zones géographiques
    if (targetUrl.includes('zones')) {
      return 'medium';
    }

    // Priorité basse par défaut
    return 'low';
  }

  private sortByPriority(a: string, b: string): number {
    const priorityOrder = { 'high': 3, 'medium': 2, 'low': 1 };
    return priorityOrder[b as keyof typeof priorityOrder] - priorityOrder[a as keyof typeof priorityOrder];
  }

  private calculateWeight(searchKeyword: string, pageKeyword: string): number {
    const searchLower = searchKeyword.toLowerCase();
    const pageLower = pageKeyword.toLowerCase();

    if (searchLower === pageLower) return 1.0;
    if (searchLower.includes(pageLower) || pageLower.includes(searchLower)) return 0.8;
    
    // Calcul de similarité simple
    const commonWords = searchLower.split(/\s+/).filter(word => 
      pageLower.split(/\s+/).includes(word)
    );
    
    return commonWords.length / Math.max(searchLower.split(/\s+/).length, pageLower.split(/\s+/).length);
  }

  private getCategoryFromUrl(url: string): string {
    if (url.includes('location')) return 'location';
    if (url.includes('demolition')) return 'demolition';
    if (url.includes('debarras')) return 'debarras';
    if (url.includes('zones')) return 'zones';
    return 'general';
  }
}

// Instance singleton
export const linkingRules = new LinkingRulesEngine();